import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import {
  ComponentsService,
  ContextualToolbarData,
  PlaylistItem,
} from '../../services/components.service';
import { UserComponentsService } from '../../template-components/services/user-components.service';
import { PlaylistService } from '../../template-components/services/playlist.service';
import { getImproveWithAiLabel } from '../../template-components/text-ai/text-ai.component';

const AI_BUTTON_TYPES = ['rise-text', 'rise-image', 'rise-folder'];
const AI_GENERATE_TYPES = ['rise-image', 'rise-folder'];
const TOOLBAR_HEIGHT = 40;
const TOOLBAR_BUTTON_PX = 32;
const TOOLBAR_GAP_PX = 4;
const TOOLBAR_PADDING_H_PX = 12;
const Y_OFFSET = 24;
/** When top and bottom placements both clip, anchor inside the component below its top edge. */
const TOP_INSET_OFFSET_PX = 8;
/** Horizontal gap between component edge and toolbar when placement is left/right. */
const SIDE_GAP_PX = Y_OFFSET;

@Component({
  selector: 'contextual-toolbar',
  templateUrl: './contextual-toolbar.component.html',
  styleUrls: ['./contextual-toolbar.component.scss'],
  standalone: false,
})
export class ContextualToolbarComponent implements OnInit, OnDestroy {
  private subscription?: Subscription;
  /** Pending `_recalculatePosition` macrotask; cleared on reschedule and destroy. */
  private positionTimeout?: ReturnType<typeof setTimeout>;

  visible = false;
  component: any = null;
  customization: {
    left: string;
    top: string;
    width: string;
    height: string;
  } | null = null;

  positionStyle: { top: string; left: string } = { top: '0px', left: '0px' };
  placement: 'top' | 'bottom' | 'left' | 'right' | 'inset-top' = 'top';
  playlistItem: PlaylistItem | null = null;

  constructor(
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private userComponentsService: UserComponentsService,
    private playlistService: PlaylistService,
    private aiCreditsService: AiCreditsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.subscription = this.componentsService.contextualToolbar$.subscribe(
      (data) => {
        this.ngZone.run(() => this._handleData(data));
      },
    );
  }

  ngOnDestroy(): void {
    this._clearPositionTimeout();
    this.subscription?.unsubscribe();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowChange(): void {
    if (this.visible) {
      this._scheduleRecalculatePosition(300);
    }
  }

  private get aiComponentType(): string | undefined {
    const type = this.component?.type;
    const tagName = this.component?.tagName;
    if (AI_BUTTON_TYPES.includes(type)) {
      return type;
    }
    if (AI_BUTTON_TYPES.includes(tagName)) {
      return tagName;
    }
    return undefined;
  }

  get showAiButton(): boolean {
    if (!this.aiComponentType || !this.aiCreditsService.canUseAi()) {
      return false;
    }

    const directive = this.componentsService.getSelectedDirective();
    return directive?.canShowAiButton?.(this.component?.id) ?? true;
  }

  get aiButtonLabel(): string {
    const type = this.aiComponentType;

    if (!!type && AI_GENERATE_TYPES.includes(type)) {
      return 'Generate with AI';
    }

    if (type === 'rise-text') {
      return getImproveWithAiLabel(
        this.attributeDataService.getAvailableAttributeData(
          this.component?.id,
          'value',
        ),
      );
    }

    return 'Improve with AI';
  }

  get showEditButtons(): boolean {
    return !!this.component && this.component.nonEditable !== false;
  }

  isDataComponent(): boolean {
    const type = this.component?.type || this.component?.tagName;
    return (
      typeof type === 'string' && type.toLowerCase().includes('rise-data-')
    );
  }

  onAiClick(): void {
    if (!this.component?.id) {
      return;
    }
    const directive = this.componentsService.getSelectedDirective();
    directive?.onAiHandler?.();
  }

  onHideClick(): void {
    if (!this.component) {
      return;
    }

    const directive = this.componentsService.getSelectedDirective();
    directive?.backToMainView?.();

    this.attributeDataService.setCustomization(
      this.component.id,
      'visibility',
      false,
    );
    this.componentsService.editHighlightedComponent(null);
    this._dismiss();
  }

  onDuplicateClick(): void {
    if (!this.component?.id) {
      return;
    }

    const directive = this.componentsService.getSelectedDirective();
    directive?.backToMainView?.();

    if (this.playlistItem) {
      this.playlistService.duplicatePlaylistItem(this.playlistItem.index);
    } else {
      this.userComponentsService.duplicateUserComponent(this.component.id);
    }
    this._dismiss();
  }

  onDeleteClick(): void {
    if (!this.component) {
      return;
    }

    const directive = this.componentsService.getSelectedDirective();
    directive?.backToMainView?.();

    if (this.playlistItem) {
      this.playlistService.deletePlaylistItem(this.playlistItem.index);
    } else {
      this.userComponentsService.removeUserComponent(this.component.id);
    }
    this.componentsService.editHighlightedComponent(null);
    this._dismiss();
  }

  private _handleData(data: ContextualToolbarData | null): void {
    if (!data || !data.id || !data.component) {
      this._clearPositionTimeout();
      this.visible = false;
      this.component = null;
      this.customization = null;
      this.playlistItem = null;
      this.placement = 'top';
      this.cdr.detectChanges();
      return;
    }

    this.playlistItem = data.playlistItem;
    if (!this.playlistItem) {
      this.playlistItem = this.componentsService.getCurrentPagePlaylistItem();
    }

    if (this.playlistItem) {
      this.component = {
        ...this.playlistService.playlistItemsToJson()[this.playlistItem.index]
          ?.element,
        id: this.playlistItem.id,
        nonEditable: true,
      };
    } else {
      this.component = data.component;
    }

    this.customization = data.customization;
    this.visible = true;
    this.cdr.detectChanges();
    this._scheduleRecalculatePosition(0);
  }

  private _clearPositionTimeout(): void {
    if (this.positionTimeout !== undefined) {
      clearTimeout(this.positionTimeout);
      this.positionTimeout = undefined;
    }
  }

  private _scheduleRecalculatePosition(delayMs: number): void {
    this._clearPositionTimeout();
    this.positionTimeout = setTimeout(() => {
      this.positionTimeout = undefined;
      this._recalculatePosition();
    }, delayMs);
  }

  private _dismiss(): void {
    this.componentsService.showContextualToolbar({ id: null });
  }

  private _recalculatePosition(): void {
    if (!this.visible || !this.customization) {
      return;
    }

    const iframe = document.querySelector(
      'iframe.presentation-preview',
    ) as HTMLIFrameElement | null;

    if (!iframe) {
      return;
    }

    const rect = iframe.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    // The iframe is sized to template coordinates (e.g. 1920x1080) and scaled
    // via a CSS transform. Use the inline width to derive the scale factor.
    const styleWidth = parseFloat(iframe.style.width) || rect.width;
    const scale = styleWidth ? rect.width / styleWidth : 1;

    const left = parseFloat(this.customization.left) || 0;
    const top = parseFloat(this.customization.top) || 0;
    const width = parseFloat(this.customization.width) || 0;
    const height = parseFloat(this.customization.height) || 0;

    const componentLeft = rect.left + left * scale;
    const componentTop = rect.top + top * scale;
    const componentWidth = width * scale;
    const componentHeight = height * scale;

    const toolbarCenterX = componentLeft + componentWidth / 2;
    const componentRight = componentLeft + componentWidth;
    const componentMidY = componentTop + componentHeight / 2;

    // Top/bottom placement centers the toolbar with translateX(-50%). Choose lateral
    // placement when that centered bar would clip the iframe, not from component extent.
    const toolbarWidthPx = this._toolbarWidthPx();
    const toolbarHalfWidth = toolbarWidthPx / 2;
    const toolbarLeftEdge = toolbarCenterX - toolbarHalfWidth;
    const toolbarRightEdge = toolbarCenterX + toolbarHalfWidth;
    const toolbarClipsRight = toolbarRightEdge > rect.right;
    const toolbarClipsLeft = toolbarLeftEdge < rect.left;

    let placement: 'top' | 'bottom' | 'left' | 'right' | 'inset-top';
    let toolbarTopPx: number;
    let toolbarLeftPx: number;

    if (toolbarClipsRight && !toolbarClipsLeft) {
      // Toolbar to the left of the component, vertically centered (CSS translateY -50%).
      placement = 'left';
      toolbarLeftPx = componentLeft - SIDE_GAP_PX;
      toolbarTopPx = componentMidY;
    } else if (toolbarClipsLeft && !toolbarClipsRight) {
      // Toolbar to the right of the component, vertically centered.
      placement = 'right';
      toolbarLeftPx = componentRight + SIDE_GAP_PX;
      toolbarTopPx = componentMidY;
    } else if (toolbarClipsRight && toolbarClipsLeft) {
      // Centered toolbar wider than the visible iframe width — anchor beside component.
      placement = 'left';
      toolbarLeftPx = componentLeft - SIDE_GAP_PX;
      toolbarTopPx = componentMidY;
    } else {
      placement = 'top';
      toolbarTopPx = componentTop - Y_OFFSET - TOOLBAR_HEIGHT;
      toolbarLeftPx = toolbarCenterX;

      // Fixed positioning is viewport-based; clip against the iframe (presentation),
      // not y=0, or the toolbar stays "above" until the whole preview is near the viewport top.
      if (toolbarTopPx < rect.top) {
        placement = 'bottom';
        toolbarTopPx = componentTop + componentHeight + Y_OFFSET;

        if (toolbarTopPx + TOOLBAR_HEIGHT > rect.bottom) {
          placement = 'inset-top';
          toolbarTopPx = componentTop + TOP_INSET_OFFSET_PX;
        }
      }
    }

    if (placement === 'left' || placement === 'right') {
      const halfToolbar = TOOLBAR_HEIGHT / 2;
      const margin = 4;
      toolbarTopPx = Math.min(
        Math.max(toolbarTopPx, rect.top + halfToolbar + margin),
        rect.bottom - halfToolbar - margin,
      );
    }

    this.placement = placement;
    this.positionStyle = {
      top: `${toolbarTopPx}px`,
      left: `${toolbarLeftPx}px`,
    };
    this.cdr.detectChanges();
  }

  /** Width of the toolbar for horizontal fit checks (measured when possible). */
  private _toolbarWidthPx(): number {
    const el = this.hostRef.nativeElement.querySelector('.contextual-toolbar');
    const measured = el?.getBoundingClientRect().width ?? 0;
    if (measured > 0) {
      return measured;
    }
    return this._estimateToolbarWidthPx();
  }

  /** Matches template button counts when the toolbar is not measurable yet. */
  private _estimateToolbarWidthPx(): number {
    let count = 1;
    if (this.showAiButton) {
      count++;
    }
    if (this.showEditButtons) {
      count += 2;
    }
    if (count === 1) {
      return TOOLBAR_HEIGHT;
    }
    return (
      TOOLBAR_PADDING_H_PX +
      count * TOOLBAR_BUTTON_PX +
      (count - 1) * TOOLBAR_GAP_PX
    );
  }
}
